import { Icon, IconID } from '@hub/common/Icon/Icon';
import { ModelViewer } from '@hub/common/ModelViewer/ModelViewer';
import { ProductMediaCarousel } from '@hub/common/ProductMediaCarousel/ProductMediaCarousel';
import type { WidenMediaElement } from '@hub/libs/widen-utils';
import { useSessionStore } from '@hub/hooks/states/useSessionStore';
import { useEffect, useRef, useState } from 'react';
import { useFetchThreeDimensionalModel } from '@hub/bearing-hub/ProductDetails/ThreeDimensionalModel/useFetchThreeDimensionalModel';
import type { IbhProductionMethods, IbhShape } from '@igus/iglidur-bearing-hub-api';
import type { IbhDimensions } from '@hub/bearing-hub/common/dimensions/dimensions';
import type { UnitSystem } from '@igus/kopla-domain';

type ProductMediaProps = {
  produceProductMedia: WidenMediaElement[];
  isCustom: boolean;
  articleNumber: string;
  shapeKey: IbhShape;
  dimensions: IbhDimensions;
  unitSystem: UnitSystem;
  productionMethod: IbhProductionMethods;
  materialColor?: string;
  onModelThumbnailGenerate?: (articleThumbnail: string) => void;
};

export const ProductMedia = ({
  produceProductMedia,
  isCustom,
  articleNumber,
  shapeKey,
  productionMethod,
  dimensions,
  unitSystem,
  materialColor,
  onModelThumbnailGenerate,
}: ProductMediaProps) => {
  const session = useSessionStore(state => state.session);
  const [showModelViewer, setShowModelViewer] = useState(isCustom);
  const [parentSize, setParentSize] = useState<{ width: number; height: number }>({
    width: 378,
    height: 526,
  });
  const parentRef = useRef<HTMLDivElement | null>(null);

  const {
    modelLink,
    fetchModelLink,
    error: fetchModelError,
  } = useFetchThreeDimensionalModel({
    articleNumber,
    session,
    shapeKey,
    dimensions,
    isCustom,
    productionMethod,
    unitSystem,
  });

  const initParentSizeObserver = (): (() => void) | undefined => {
    const el = parentRef.current;
    if (!el) {
      return undefined;
    }

    const updateSize = () => {
      setParentSize({
        width: el.clientWidth,
        height: el.clientHeight,
      });
    };

    updateSize();

    const resizeObserver = new ResizeObserver(() => updateSize());
    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
    };
  };

  useEffect(() => {
    if (fetchModelError || modelLink === null) {
      setShowModelViewer(false);
    }
  }, [fetchModelError, modelLink]);

  useEffect(() => {
    if (!modelLink && showModelViewer) {
      void fetchModelLink();
    }
  }, [showModelViewer, session]);

  useEffect(() => {
    return initParentSizeObserver();
  }, []);

  const modelViewerSize = {
    width: parentSize.width || 378,
    height: parentSize.height || 526,
  };

  return (
    <div ref={parentRef} className="relative w-full h-full">
      {showModelViewer ? (
        <ModelViewer
          configuration={{
            materialColor: materialColor || '#3d3d3d',
            modelUrl: modelLink,
            fileType: 'stl',
            width: modelViewerSize.width,
            height: modelViewerSize.height,
          }}
          onModelThumbnailGenerate={onModelThumbnailGenerate}
        >
          <div
            className="absolute bottom-[10px] right-[10px] cursor-pointer z-[1]"
            onClick={() => setShowModelViewer(false)}
          >
            <Icon size={10} id={IconID.PHOTO_LIBRARY} primary={false} />
          </div>
        </ModelViewer>
      ) : (
        !!produceProductMedia?.length && (
          <ProductMediaCarousel
            media={produceProductMedia}
            onShowModalViewer={() => setShowModelViewer(true)}
            showModelViewer={!fetchModelError}
          />
        )
      )}
    </div>
  );
};
